import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, EnvelopeOpenIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface EmailVerificationWarningProps {
  location?: 'header' | 'profile';
}

const EmailVerificationWarning: React.FC<EmailVerificationWarningProps> = ({ location = 'header' }) => {
  const { firebaseUser, sendVerification } = useAuth();
  const [isResending, setIsResending] = useState(false);
  
  // Check if warning was dismissed from header
  const [isDismissed, setIsDismissed] = useState(
    localStorage.getItem('emailVerificationWarningDismissed') === 'true'
  );
  
  // Show warning if:
  // - On profile page (always show if email not verified)
  // - On header page AND not dismissed from header
  const shouldShow = firebaseUser && 
                    !firebaseUser.emailVerified && 
                    (location === 'profile' || !isDismissed);

  // Auto-refresh every 30 seconds to check if email has been verified
  useEffect(() => {
    if (!firebaseUser || firebaseUser.emailVerified) return;

    const interval = setInterval(async () => {
      try {
        await firebaseUser.reload();
      } catch (error) {
        console.error('Auto-refresh error:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [firebaseUser]);

  if (!shouldShow) return null;

  const handleResendVerification = async () => {
    if (isResending) return;
    setIsResending(true);
    
    const result = await sendVerification();
    
    if (result.success) {
      toast.success('Verification email sent! Please check your inbox and especially your spam folder.', {
        icon: '📧',
        duration: 6000,
      });
    } else {
      toast.error(result.error || 'Failed to send verification email');
    }
    
    setIsResending(false);
  };

  const handleDismiss = () => {
    if (location === 'header') {
      localStorage.setItem('emailVerificationWarningDismissed', 'true');
      setIsDismissed(true);
    }
  };

  return (
    <div className={`
      relative overflow-hidden transition-all duration-500 ease-in-out
      ${location === 'header' 
        ? 'bg-gradient-to-r from-blue-600/95 via-indigo-600/95 to-purple-600/95 backdrop-blur-md border-b border-white/20' 
        : 'bg-white/80 backdrop-blur-md rounded-2xl border border-blue-100 shadow-sm p-4 mt-4'}
    `}>
      {/* Decorative background pulse */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-48 h-48 bg-blue-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className={`${location === 'header' ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : ''} relative z-10`}>
        <div className={`flex flex-col sm:flex-row items-center justify-between ${location === 'header' ? 'py-2.5 sm:py-2' : ''} gap-3 sm:gap-4`}>
          <div className="flex items-center space-x-3">
            <div className={`
              flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
              ${location === 'header' ? 'bg-white/20' : 'bg-blue-100'}
            `}>
              {firebaseUser?.emailVerified ? (
                <CheckCircleIcon className={`h-6 w-6 ${location === 'header' ? 'text-white' : 'text-blue-600'}`} />
              ) : (
                <EnvelopeOpenIcon className={`h-6 w-6 ${location === 'header' ? 'text-white' : 'text-blue-600'} animate-bounce`} />
              )}
            </div>
            
            <div className="text-center sm:text-left">
              <p className={`font-semibold ${location === 'header' ? 'text-white' : 'text-slate-900'} text-sm sm:text-base flex items-center justify-center sm:justify-start`}>
                Verify your email address
              </p>
              <p className={`text-xs sm:text-sm ${location === 'header' ? 'text-blue-100' : 'text-slate-500'} mt-0.5`}>
                A link was sent to <span className="font-semibold underline decoration-blue-400/30 underline-offset-2">{firebaseUser?.email}</span>. 
                <span className="block sm:inline sm:ml-1 font-medium italic opacity-90">
                  (Check your <span className={location === 'header' ? 'text-white' : 'text-blue-600'}>Spam</span> folder if you don't see it)
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3 w-full sm:w-auto justify-center sm:justify-end">
            <button
              onClick={handleResendVerification}
              disabled={isResending}
              className={`
                px-5 py-2 rounded-xl text-sm font-bold transition-all duration-300 transform active:scale-95
                ${location === 'header' 
                  ? 'bg-white text-blue-600 hover:bg-blue-50 hover:shadow-lg shadow-blue-900/20' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md shadow-blue-600/20'}
                disabled:opacity-50 disabled:cursor-not-allowed flex items-center
              `}
            >
              {isResending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                'Resend Link'
              )}
            </button>
            
            {location === 'header' && (
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white transition-colors duration-200 p-2 rounded-full hover:bg-white/10"
                aria-label="Dismiss"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationWarning;
